// `extension-automation-copilot` — the second half of the automation screen: whether Copilot
// runs at all, who it emails, and which jobs it may act on.
//
// ⚠️ ITS JOB-FILTERS BLOCK IS THE CANDIDATE BLOCK, CHARACTER FOR CHARACTER. A
// whitespace-insensitive diff of the two `woo-collapsible`s in
// `extension-automation-copilot.tpl.html` and `extension-automation-candidate-criteria.tpl.html`
// is the title ("job filters" against "candidate filters") and which array they repeat over.
// Both are `AtsFiltersSection`, which the criteria builder already ports — a second copy here
// would be a third rendering of the same table.
//
// ⚠️ AND THE TWO TABLES ASK DIFFERENT ENTITIES FOR THEIR FIELDS. The copilot's configuration is
// `getConfigurationsByWhitelabel(JOB)` and the candidate block's is the default (CANDIDATE), so
// a tenant with filterable job fields and no candidate ones renders one table and not the other.
// Sharing the SECTION component while sharing the CONFIGURATION would put Bullhorn's job fields
// in the candidate filter.
import { createElement, type ReactElement } from 'react'
import type { AtsFiltersFieldConfiguration, AtsFiltersSingleselectValues } from '@woo-ext/api'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { WooCollapsible } from '../common/WooCollapsible.tsx'
import { WooSwitch } from '../common/WooSwitch.tsx'
import { AtsFiltersSection } from '../search/AtsFiltersSection.tsx'
import type { AtsFilterRow } from '../search/atsFilterData.ts'

export interface AutomationCopilotProps {
  active?: boolean
  sendToJobOwner?: boolean
  sendToAssignedUsers?: boolean
  /** The comma-joined form value, not the DTO's array — see `AutomationCopilotConfig`. */
  additionalRecipients?: string
  backupRecipient?: string
  /** `ctrl.atsLabel` — the collapsible's title is "<ATS> job filters". */
  atsLabel?: string
  /**
   * `assignedUsersEnabled = atsName === ATSType.BULLHORN` — the IDENTITY, which the caller resolves.
   *
   * ⚠️ A CHECKBOX GATED ON THE ATS BY NAME, not on a feature flag: only Bullhorn has the notion
   * of users assigned to a job, so on every other ATS the second checkbox is absent rather than
   * disabled. Deriving it from anything else puts a control on a tenant whose ATS cannot answer.
   */
  assignedUsersEnabled?: boolean
  jobFilterRows?: readonly AtsFilterRow[]
  jobFilterFields?: readonly AtsFiltersFieldConfiguration[]
  singleselectValues?: AtsFiltersSingleselectValues
  onActiveChange?: (active: boolean) => void
  onSendToJobOwnerChange?: (value: boolean) => void
  onSendToAssignedUsersChange?: (value: boolean) => void
  onAdditionalRecipientsChange?: (value: string) => void
  onBackupRecipientChange?: (value: string) => void
  onJobFilterChange?: (index: number, row: AtsFilterRow) => void
  onJobFilterRemove?: (index: number) => void
  onJobFilterAdd?: () => void
}

export function AutomationCopilot({
  active = false,
  sendToJobOwner = false,
  sendToAssignedUsers = false,
  additionalRecipients = '',
  backupRecipient = '',
  atsLabel,
  assignedUsersEnabled = false,
  jobFilterRows = [],
  jobFilterFields = [],
  singleselectValues = {},
  onActiveChange,
  onSendToJobOwnerChange,
  onSendToAssignedUsersChange,
  onAdditionalRecipientsChange,
  onBackupRecipientChange,
  onJobFilterChange,
  onJobFilterRemove,
  onJobFilterAdd,
}: AutomationCopilotProps): ReactElement {
  // ⚠️ THE HOST ELEMENT AGAIN, and this time it is the one carrying the column layout:
  // `.extension-automation-criteria .criteria-container extension-automation-copilot-component
  // { display: flex; flex-direction: column; margin-top: 20px; }`. A React tree that renders
  // only the inner `div.extension-automation-copilot` loses the 20px gap under the candidate
  // half and the flex context the card sits in — the fourteenth time in this port that a
  // component ELEMENT, not a class, is what a stylesheet is written against.
  return createElement(
    'extension-automation-copilot-component',
    null,
    <div className="extension-automation-copilot">
      <div className="copilot-title-section">
        <div className="copilot-title">Copilot</div>
        <div className="copilot-switch">
          {/* The label is the STATE's name, not the action's: "Active" when on. */}
          <WooSwitch
            className="switch-small"
            isOn={active}
            label={active ? 'Active' : 'Inactive'}
            onToggle={() => {
              onActiveChange?.(!active)
            }}
          />
        </div>
      </div>
      <div className="copilot-content">
        <div className="email-recipients-wrap">
          <div className="recipients">
            <label>Send emails to</label>
            <div>
              <WooCheckbox
                checked={sendToJobOwner}
                onChange={(next) => {
                  onSendToJobOwnerChange?.(next)
                }}
              >
                Job owner
              </WooCheckbox>{' '}
              {/* ⚠️ THE SPACE BETWEEN THE TWO BOXES IS A TEXT NODE. Upstream has the two
                  `<checkbox>` elements on their own lines, and the newline between them
                  collapses to one space in the inline formatting context; JSX drops it, and the
                  second checkbox sat ~4px left of where production has it. Sixth time in this
                  port — and here it is 492 pixels, the whole residual on the screen. */}
              {!assignedUsersEnabled ? null : (
                <WooCheckbox
                  checked={sendToAssignedUsers}
                  onChange={(next) => {
                    onSendToAssignedUsersChange?.(next)
                  }}
                >
                  Job assigned users
                </WooCheckbox>
              )}
            </div>
            <input
              className="form-control input"
              type="text"
              name="additionalRecipients"
              maxLength={255}
              autoComplete="off"
              value={additionalRecipients}
              onChange={(event) => {
                onAdditionalRecipientsChange?.(event.target.value)
              }}
            />
          </div>
          <div className="backup-recipient">
            <label>Backup recipient</label>
            {/* `type="email"`, where the one above is `type="text"` — the browser validates this
                field and not the recipient list, which holds several addresses. */}
            <input
              className="form-control input"
              type="email"
              name="backupRecipient"
              maxLength={255}
              autoComplete="off"
              value={backupRecipient}
              onChange={(event) => {
                onBackupRecipientChange?.(event.target.value)
              }}
            />
          </div>
        </div>
        {/* `ng-if` on the configuration having fields: a tenant whose ATS exposes none renders
            no collapsible at all, not an empty one. */}
        {jobFilterFields.length === 0 ? null : (
          <WooCollapsible id="ats-filters-criteria-id" className="ats-filters" title={`${atsLabel ?? ''} job filters`} startOpen>
            <div className="ats-filters-wrap">
              <AtsFiltersSection
                rows={jobFilterRows}
                fields={jobFilterFields}
                singleselectValues={singleselectValues}
                {...(onJobFilterChange === undefined ? {} : { onChange: onJobFilterChange })}
                {...(onJobFilterRemove === undefined ? {} : { onRemove: onJobFilterRemove })}
                {...(onJobFilterAdd === undefined ? {} : { onAdd: onJobFilterAdd })}
              />
            </div>
          </WooCollapsible>
        )}
      </div>
    </div>,
  )
}
